import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Card,
	Checkbox,
	ClipboardButton,
	Container,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Input,
	Label,
	Paragraph,
	Separator,
	Spinner,
	Switch,
	Table,
	tokens,
} from '@treams/treams-ui';

const FavIconImage = styled.img`
	user-select: none;
	pointer-events: none;
	border-radius: 4px;
	border: 1px solid ${tokens.border.subtle};
`;

function FavIcon(props: { domain: string }) {
	const size = 24;
	const minImageSize = window.devicePixelRatio * size;
	const multiplesOf = 48;
	const imageSize = Math.ceil(minImageSize / multiplesOf) * multiplesOf;

	const favIconUrl = `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${props.domain}&size=${imageSize}`;

	return <FavIconImage src={favIconUrl} width={size} height={size} onError={() => {}} />;
}

const statusText = {
	[graphql.DomainVerificationStatus.Verified]: 'verified',
	[graphql.DomainVerificationStatus.Pending]: 'pending',
	[graphql.DomainVerificationStatus.Failed]: 'failed',
};

const StatusBullet = styled.div<{ status: graphql.DomainVerificationStatus }>`
	width: 12px;
	height: 12px;
	border-radius: 50%;
	margin-right: 8px;
	background: ${(props) =>
		props.status === graphql.DomainVerificationStatus.Verified
			? props.theme.colors.green
			: props.status === graphql.DomainVerificationStatus.Pending
				? props.theme.colors.yellow
				: props.theme.colors.red};
`;

const getDKIMRecords = (
	domain: string,
	emailDomain: string,
	dkimTokens: string[],
	appendsHostName: boolean,
) => {
	const subdomain = emailDomain.split('.').slice(0, -domain.split('.').length).join('.');
	const append = appendsHostName ? subdomain : emailDomain;

	return dkimTokens.map(
		(token) =>
			({
				type: 'CNAME',
				name: append === '' ? `${token}._domainkey` : `${token}._domainkey.${append}`,
				value: `${token}.dkim.amazonses.com.`,
			}) as DNSRecord,
	);
};

const getMailFromRecords = (domain: string, mailFromDomain: string, appendsHostName: boolean) => {
	const subdomain = mailFromDomain.split('.').slice(0, -domain.split('.').length).join('.');

	return [
		{
			type: 'MX',
			name: appendsHostName ? subdomain : mailFromDomain,
			value: '10 feedback-smtp.eu-west-1.amazonses.com.',
		},
		{
			type: 'TXT',
			name: appendsHostName ? subdomain : mailFromDomain,
			value: 'v=spf1 include:amazonses.com -all',
		},
	] as DNSRecord[];
};

type DNSRecord = {
	type: 'MX' | 'TXT' | 'CNAME';
	name: string;
	value: string;
};

const RecordTypeLabel = styled.span`
	color: ${tokens.text.primary};
	font-weight: bold;
`;

const RecordElement = styled.span`
	display: block;
	color: ${tokens.text.secondary};
	word-wrap: break-word;
	line-clamp: 2;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	flex-grow: 1;
`;

function CopyableRecordElement(props: { children: string }) {
	return (
		<Flex alignItems="center" width="100%">
			<RecordElement>{props.children}</RecordElement>
			<ClipboardButton text={props.children} ml={1} small />
		</Flex>
	);
}

function DNSVerificationCard(props: {
	type: 'dkim' | 'mailFrom';
	verificationStatus: graphql.DomainVerificationStatus;
	dnsRecords: DNSRecord[];
}) {
	const { t } = useTranslation('', { keyPrefix: 'settings.notifications.customEmailDomain' });

	const typeTitle = t(`verificationType.${props.type}.title`);

	const downloadRecords = () => {
		const blob = new Blob(
			[
				props.dnsRecords
					.map((record) => `${record.type}\n${record.name}\n${record.value}`)
					.join('\n\n'),
			],
			{
				type: 'text/plain;charset=utf-8',
			},
		);

		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `${typeTitle.split(' ').join('-')}-DNS-records-Treams.txt`;
		a.click();

		URL.revokeObjectURL(url);
	};

	return (
		<Card mt={3}>
			<Flex alignItems="center" mb={3}>
				<StatusBullet status={props.verificationStatus} />
				<Heading3 flexGrow={1}>{typeTitle}</Heading3>
				<Paragraph>{t(`verificationStatus.${statusText[props.verificationStatus]}`)}</Paragraph>
			</Flex>
			<Table>
				{props.dnsRecords.map((record, i) => (
					<Table.Row key={i}>
						<Table.Cell basis={1 / 9} tightLayout>
							<RecordTypeLabel>{record.type}</RecordTypeLabel>
						</Table.Cell>
						<Table.Cell basis={4 / 9} tightLayout>
							<CopyableRecordElement>{record.name}</CopyableRecordElement>
						</Table.Cell>
						<Table.Cell basis={4 / 9} tightLayout>
							<CopyableRecordElement>{record.value}</CopyableRecordElement>
						</Table.Cell>
					</Table.Row>
				))}
			</Table>
			<Flex mt={3}>
				<Button
					flexShrink={1}
					small
					type="secondary"
					title={t('downloadRecords', { type: typeTitle })}
					onClick={downloadRecords}
				/>
			</Flex>
		</Card>
	);
}

export default function NotificationsCustomEmailDomain() {
	const { t } = useTranslation('', { keyPrefix: 'settings.notifications.customEmailDomain' });
	const mailFromOptional = useFeature(graphql.FeatureName.CustomEmailDomainOptionalMailfrom);

	const { data, loading: isLoading } = graphql.useWorkspaceCustomEmailDomainQuery({
		fetchPolicy: 'network-only',
	});

	const [update, { loading: isUpdating, error: updateError }] =
		graphql.useUpdateWorkspaceCustomEmailDomainMutation();
	const [reset, { loading: isResetting, error: resetError }] =
		graphql.useResetWorkspaceCustomEmailDomainMutation();
	const [setEnabled, { loading: isSettingEnabled }] =
		graphql.useSetWorkspaceCustomEmailDomainEnabledMutation();

	const customEmailDomain = data?.viewer?.workspace?.customEmailDomain;

	const domain = customEmailDomain?.domain;

	const [newDomain, setNewDomain] = useState<string | null>(null);
	const [senderName, setSenderName] = useState('');
	const [senderAddress, setSenderAddress] = useState('');

	useEffect(() => {
		setSenderName(customEmailDomain?.senderName ?? '');
		setSenderAddress(customEmailDomain?.senderAddress ?? '');
	}, [customEmailDomain]);

	const setValidSenderName = useCallback(
		(value: string) => {
			setSenderName(value.replace(/[^ -~]/g, ''));
		},
		[setSenderName],
	);

	const setValidSenderAddress = useCallback(
		(value: string) => {
			if (/^[a-zA-Z0-9._%+-]*$/.test(value)) {
				setSenderAddress(value);
			}
		},
		[setSenderAddress],
	);

	const validDomain = newDomain?.match(
		/^([a-z0-9][a-z0-9-]{0,61})?[a-z0-9]\.[a-z]{2,6}(\.[a-z]{2,5})?$/,
	);

	const onSubmit = async () => {
		if (!newDomain) {
			return;
		}

		await update({
			variables: {
				input: {
					domain: newDomain,
				},
			},
		});
	};

	const displayDomain = domain ?? newDomain ?? '';
	const loading = isLoading || isUpdating || isResetting;

	const [appendsHostName, setAppendsHostName] = useState(true);

	const dkimRecords = useMemo(
		() =>
			customEmailDomain
				? getDKIMRecords(
						customEmailDomain.domain,
						customEmailDomain.emailDomain,
						customEmailDomain.dkimTokens,
						appendsHostName,
					)
				: [],
		[customEmailDomain, appendsHostName],
	);

	const mailFromRecords = useMemo(
		() =>
			customEmailDomain && !mailFromOptional
				? getMailFromRecords(
						customEmailDomain.domain,
						customEmailDomain.mailFromDomain,
						appendsHostName,
					)
				: [],
		[customEmailDomain, appendsHostName, mailFromOptional],
	);

	const needsReset =
		!!customEmailDomain &&
		(customEmailDomain.dkimVerificationStatus === graphql.DomainVerificationStatus.Failed ||
			(!mailFromOptional &&
				customEmailDomain.mailFromVerificationStatus === graphql.DomainVerificationStatus.Failed));

	const verified =
		!!customEmailDomain &&
		customEmailDomain.dkimVerificationStatus === graphql.DomainVerificationStatus.Verified &&
		(mailFromOptional ||
			customEmailDomain.mailFromVerificationStatus === graphql.DomainVerificationStatus.Verified);

	return (
		<Container pt={`${tokens.gap.page}px`} pb={4}>
			<Box mb={4}>
				<Heading>{t('heading')}</Heading>
				<Paragraph mt={2}>{t('description')}</Paragraph>
			</Box>
			<Separator />
			<Box my={4}>
				<Paragraph mb={4}>{t('explanation')}</Paragraph>
				<Label mb={2}>{t('domain.label')}</Label>
				<Flex alignItems="flex-start">
					<Input
						px={1}
						icon={<FavIcon domain={displayDomain ?? ''} />}
						placeholder={t('domain.placeholder')}
						loading={loading}
						disabled={!!domain}
						value={displayDomain}
						onChange={setNewDomain}
						error={newDomain && !validDomain ? t('domain.invalid') : undefined}
					/>
					<Button
						type={domain ? 'destructive' : 'primary'}
						title={t(domain ? 'domain.reset' : 'domain.verify')}
						ml={3}
						height={40}
						disabled={loading}
						loading={isUpdating || isResetting}
						onClick={() => {
							if (domain) {
								reset();
							} else {
								onSubmit();
							}
						}}
					/>
				</Flex>
				{resetError && <GraphQLErrorMessage error={resetError.graphQLErrors[0]} />}
				{updateError && <GraphQLErrorMessage error={updateError.graphQLErrors[0]} />}
				{customEmailDomain && (
					<Box mt={4}>
						<Paragraph mb={3}>{t('dnsInstructions')}</Paragraph>
						<Flex mb={4}>
							<Checkbox
								checked={!appendsHostName}
								onClick={() => setAppendsHostName(!appendsHostName)}
								mr={2}
							/>
							<Paragraph>{t('dnsAppendsHostName')}</Paragraph>
						</Flex>
						<DNSVerificationCard
							type="dkim"
							verificationStatus={customEmailDomain.dkimVerificationStatus}
							dnsRecords={dkimRecords}
						/>
						{!mailFromOptional && (
							<DNSVerificationCard
								type="mailFrom"
								verificationStatus={customEmailDomain.mailFromVerificationStatus}
								dnsRecords={mailFromRecords}
							/>
						)}
						<Card mt={3}>
							<Heading3 flexGrow={1}>{t('sender.title')}</Heading3>
							<Paragraph mt={1}>{t('sender.description')}</Paragraph>
							<Flex mt={3}>
								<Box mr={2} flexGrow={1}>
									<Label mb={2}>{t('sender.name.label')}</Label>
									<Input
										placeholder={t('sender.name.placeholder')}
										loading={loading}
										disabled={customEmailDomain.enabled}
										value={senderName}
										onChange={setValidSenderName}
									/>
								</Box>
								<Box flexGrow={1}>
									<Label mb={2}>
										{t('sender.address.label', { emailDomain: customEmailDomain.emailDomain })}
									</Label>
									<Input
										placeholder={t('sender.address.placeholder')}
										loading={loading}
										disabled={customEmailDomain.enabled}
										value={senderAddress}
										onChange={setValidSenderAddress}
									/>
								</Box>
							</Flex>
							{customEmailDomain.enabled && (
								<Callout color="warning" mt={3}>
									<Paragraph>{t('sender.notEditable')}</Paragraph>
								</Callout>
							)}
						</Card>
						{needsReset ? (
							<Box mt={4}>
								<Callout color="red">
									<Paragraph>{t('resetInstructions')}</Paragraph>
								</Callout>
							</Box>
						) : (
							<Flex
								mt={4}
								opacity={verified ? 1.0 : 0.4}
								alignItems="center"
								style={{
									pointerEvents: verified ? 'unset' : 'none',
									userSelect: verified ? 'unset' : 'none',
								}}
							>
								<Paragraph flexGrow={1} mr={3}>
									{t('verifiedInstructionsV2', {
										emailDomain: customEmailDomain.emailDomain,
										senderAddress: senderAddress || t('sender.address.placeholder'),
									})}
								</Paragraph>
								<Flex flexShrink={0} alignItems="center">
									<Spinner mx={2} opacity={isSettingEnabled ? 1.0 : 0.0} />
									<Switch
										on={customEmailDomain.enabled}
										onChange={(on) =>
											setEnabled({
												variables: {
													input: {
														enabled: on,
														senderName,
														senderAddress,
													},
												},
											})
										}
										tintColor="green"
										large
									/>
								</Flex>
							</Flex>
						)}
					</Box>
				)}
			</Box>
		</Container>
	);
}
